import { Check, ChevronDown, ChevronUp, Loader2, Plus } from 'lucide-react'
import { startTransition, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  useAddTenantLanguageMutation,
  useDeleteTenantLanguageMutation,
  useAllTenantLanguagesQuery,
  useReorderTenantLanguagesMutation,
  useUpdateTenantDefaultLanguageMutation,
} from '@/entities/tenant'
import { AVAILABLE_LANGUAGES } from '@/shared/constants/languages'
import { useLanguageLabel } from '@/shared/hooks/use-language-label'
import { getApiErrorText } from '@/shared/libs/get-api-error-text'
import { showToast } from '@/shared/libs/toast'
import { Button } from '@/shared/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/shared/ui/sheet'

interface TenantLanguageConfigProps {
  tenantId: string
  initialLanguages?: string[]
  initialDefault?: string
}

const EMPTY_LANGUAGES: string[] = []

export function TenantLanguageConfig({
  tenantId,
  initialLanguages = EMPTY_LANGUAGES,
  initialDefault,
}: TenantLanguageConfigProps) {
  const { t } = useTranslation()
  const languageLabel = useLanguageLabel()
  const addMutation = useAddTenantLanguageMutation()
  const deleteMutation = useDeleteTenantLanguageMutation()
  const reorderMutation = useReorderTenantLanguagesMutation()
  const updateDefaultMutation = useUpdateTenantDefaultLanguageMutation()
  const { data: tenantLanguages } = useAllTenantLanguagesQuery(tenantId)

  const [selectedLanguages, setSelectedLanguages] = useState<string[]>(initialLanguages)
  const [defaultLanguage, setDefaultLanguage] = useState<string | undefined>(initialDefault)
  const [isLanguagePickerOpen, setIsLanguagePickerOpen] = useState(false)
  const [searchKeyword, setSearchKeyword] = useState('')
  const [deletingCodes, setDeletingCodes] = useState<Set<string>>(() => new Set())
  const [addingCodes, setAddingCodes] = useState<Set<string>>(() => new Set())
  const [reorderingCodes, setReorderingCodes] = useState<Set<string>>(() => new Set())
  const [languageIdByCode, setLanguageIdByCode] = useState<Map<string, string>>(() => new Map())

  useEffect(() => {
    // * VI: Reset state ngay khi đổi tenant để tránh flash state của tenant cũ trước khi query mới trả về. | EN: Reset local state immediately on tenant change to avoid flashing previous tenant state before fresh query resolves.
    startTransition(() => {
      setSelectedLanguages(initialLanguages)
      setDefaultLanguage(initialDefault)
      setSearchKeyword('')
      setIsLanguagePickerOpen(false)
      setDeletingCodes(new Set())
      setAddingCodes(new Set())
      setReorderingCodes(new Set())
    })
  }, [initialDefault, initialLanguages, tenantId])

  useEffect(() => {
    if (!tenantLanguages?.length) {
      return
    }

    const activeLanguages = tenantLanguages
      .filter((item) => item.isActive)
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((item) => item.languageCode)
      .filter((code): code is string => Boolean(code))
      .filter((code, index, codes) => codes.indexOf(code) === index)

    const defaultLang = tenantLanguages.find((item) => item.isDefault)?.languageCode

    const idByCode = new Map(tenantLanguages.map((item) => [item.languageCode, item.id]))

    startTransition(() => {
      setSelectedLanguages(activeLanguages)
      setDefaultLanguage(defaultLang ?? activeLanguages[0] ?? undefined)
      setLanguageIdByCode(idByCode)
    })
  }, [tenantLanguages])

  useEffect(() => {
    if (!defaultLanguage && selectedLanguages.length > 0) {
      startTransition(() => {
        setDefaultLanguage(selectedLanguages[0])
      })
    }
  }, [defaultLanguage, selectedLanguages])

  async function removeLanguage(code: string) {
    const tenantLanguageId = languageIdByCode.get(code)
    if (!tenantLanguageId) {
      setSelectedLanguages((prev) => prev.filter((l) => l !== code))
      return
    }

    setDeletingCodes((prev) => new Set(prev).add(code))
    setSelectedLanguages((prev) => prev.filter((l) => l !== code))
    try {
      await deleteMutation.mutateAsync({ id: tenantId, tenantLanguageId })
      if (defaultLanguage === code) {
        setDefaultLanguage(undefined)
      }
    } catch (error) {
      setSelectedLanguages((prev) => (prev.includes(code) ? prev : [...prev, code]))
      showToast.error(getApiErrorText(error) ?? t('common.operationFailed'))
    } finally {
      setDeletingCodes((prev) => {
        const next = new Set(prev)
        next.delete(code)
        return next
      })
    }
  }

  async function addLanguage(code: string) {
    setAddingCodes((prev) => new Set(prev).add(code))
    setSelectedLanguages((prev) => (prev.includes(code) ? prev : [...prev, code]))
    try {
      await addMutation.mutateAsync({ id: tenantId, languageCode: code })
    } catch (error) {
      setSelectedLanguages((prev) => prev.filter((l) => l !== code))
      showToast.error(getApiErrorText(error) ?? t('common.operationFailed'))
    } finally {
      setAddingCodes((prev) => {
        const next = new Set(prev)
        next.delete(code)
        return next
      })
    }
  }

  function handleLanguageToggle(code: string) {
    if (selectedLanguages.includes(code)) {
      void removeLanguage(code)
    } else {
      void addLanguage(code)
    }
  }

  async function moveLanguage(index: number, direction: -1 | 1) {
    const targetIndex = index + direction
    if (targetIndex < 0 || targetIndex >= selectedLanguages.length) {
      return
    }

    const codeA = selectedLanguages[index]
    const codeB = selectedLanguages[targetIndex]
    if (!codeA || !codeB) {
      return
    }

    const idA = languageIdByCode.get(codeA)
    const idB = languageIdByCode.get(codeB)
    if (!idA || !idB) {
      return
    }

    const previous = selectedLanguages
    const updated = [...selectedLanguages]
    updated[index] = codeB
    updated[targetIndex] = codeA

    setReorderingCodes((prev) => new Set(prev).add(codeA).add(codeB))
    setSelectedLanguages(updated)
    try {
      await reorderMutation.mutateAsync({
        id: tenantId,
        updates: [
          { tenantLanguageId: idA, sortOrder: targetIndex },
          { tenantLanguageId: idB, sortOrder: index },
        ],
      })
    } catch (error) {
      setSelectedLanguages(previous)
      showToast.error(getApiErrorText(error) ?? t('common.operationFailed'))
    } finally {
      setReorderingCodes((prev) => {
        const next = new Set(prev)
        next.delete(codeA)
        next.delete(codeB)
        return next
      })
    }
  }

  function moveUp(index: number) {
    void moveLanguage(index, -1)
  }

  function moveDown(index: number) {
    void moveLanguage(index, 1)
  }

  function openLanguagePicker() {
    setSearchKeyword('')
    setIsLanguagePickerOpen(true)
  }

  async function handleDefaultLanguageChange(languageCode: string) {
    if (languageCode === defaultLanguage) {
      return
    }

    const previous = defaultLanguage
    setDefaultLanguage(languageCode)
    try {
      await updateDefaultMutation.mutateAsync({ id: tenantId, languageCode })
      showToast.success(t('tenant.language.saveSuccess'))
    } catch (error) {
      setDefaultLanguage(previous)
      showToast.error(getApiErrorText(error) ?? t('common.operationFailed'))
    }
  }

  function getLangLabel(code: string): string {
    return languageLabel(code)
  }

  const normalizedKeyword = searchKeyword.trim().toLowerCase()
  const filteredLanguages = AVAILABLE_LANGUAGES.filter((lang) => {
    if (!normalizedKeyword) {
      return true
    }
    const displayName = getLangLabel(lang.code).toLowerCase()
    const code = lang.code.toLowerCase()
    return displayName.includes(normalizedKeyword) || code.includes(normalizedKeyword)
  })

  const allSelected =
    AVAILABLE_LANGUAGES.length > 0 && selectedLanguages.length === AVAILABLE_LANGUAGES.length

  function handleToggleSelectAll() {
    if (allSelected) {
      selectedLanguages.forEach((code) => void removeLanguage(code))
      return
    }
    AVAILABLE_LANGUAGES.forEach((lang) => {
      if (!selectedLanguages.includes(lang.code)) {
        void addLanguage(lang.code)
      }
    })
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Selected languages + Add action */}
      <div>
        <div className="mb-2 flex items-center justify-between gap-3">
          <p className="text-sm font-medium">{t('tenant.language.supported')}</p>
          <Button type="button" variant="outline" onClick={openLanguagePicker}>
            <Plus className="mr-1.5 h-4 w-4" />
            {t('tenant.language.addMore', { defaultValue: 'Add language' })}
          </Button>
        </div>
        {selectedLanguages.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {selectedLanguages.map((code) => (
              <span
                key={code}
                className="inline-flex items-center rounded-md border border-zinc-200 bg-zinc-50 px-3 py-1.5 text-sm text-zinc-700"
              >
                {getLangLabel(code)}
              </span>
            ))}
          </div>
        ) : (
          <p className="text-sm text-zinc-500">
            {t('tenant.language.noneSelected', { defaultValue: 'No language selected yet.' })}
          </p>
        )}
      </div>

      <Sheet open={isLanguagePickerOpen} onOpenChange={setIsLanguagePickerOpen}>
        <SheetContent side="right" className="w-full p-0 sm:max-w-[720px]">
          <div className="flex h-full flex-col">
            <SheetHeader className="px-6 pt-6">
              <SheetTitle>
                {t('tenant.language.manageTitle', { defaultValue: 'Manage Languages' })}
              </SheetTitle>
              <SheetDescription>
                {t('tenant.language.manageDescription', {
                  defaultValue: 'Changes are saved instantly.',
                })}
              </SheetDescription>
            </SheetHeader>

            <div className="flex-1 space-y-5 overflow-y-auto px-6 pb-4">
              <div>
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm font-medium">
                    {t('tenant.language.selectLabel', { defaultValue: 'Select languages' })}
                  </p>
                  <Button type="button" variant="outline" size="sm" onClick={handleToggleSelectAll}>
                    {allSelected
                      ? t('tenant.language.clearAll', { defaultValue: 'Clear all' })
                      : t('tenant.language.selectAll', { defaultValue: 'Select all' })}
                  </Button>
                </div>
                <input
                  type="text"
                  value={searchKeyword}
                  onChange={(e) => setSearchKeyword(e.target.value)}
                  placeholder={t('tenant.language.searchPlaceholder', {
                    defaultValue: 'Search language...',
                  })}
                  className="mb-2 h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
                <div className="max-h-56 overflow-y-auto rounded-md border border-zinc-200 p-2">
                  <div className="flex flex-wrap gap-2">
                    {filteredLanguages.map((lang) => {
                      const isSelected = selectedLanguages.includes(lang.code)
                      const isDeleting = deletingCodes.has(lang.code)
                      const isAdding = addingCodes.has(lang.code)
                      return (
                        <button
                          key={lang.code}
                          type="button"
                          disabled={isDeleting || isAdding}
                          onClick={() => handleLanguageToggle(lang.code)}
                          className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                            isSelected
                              ? 'border-primary bg-primary/10 text-primary'
                              : 'border-zinc-200 bg-white text-zinc-600 hover:border-zinc-300'
                          }`}
                        >
                          {isDeleting || isAdding ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            isSelected && <Check className="h-3.5 w-3.5" />
                          )}
                          <span>{getLangLabel(lang.code)}</span>
                        </button>
                      )
                    })}
                    {filteredLanguages.length === 0 && (
                      <p className="px-2 py-1 text-sm text-zinc-500">
                        {t('tenant.language.noSearchResult', {
                          defaultValue: 'No language found.',
                        })}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <p className="mb-2 text-sm font-medium">{t('tenant.language.order')}</p>
                {selectedLanguages.length > 0 ? (
                  <div className="flex flex-col gap-2">
                    {selectedLanguages.map((code, index) => {
                      const isBusy =
                        deletingCodes.has(code) ||
                        addingCodes.has(code) ||
                        reorderingCodes.has(code)
                      return (
                        <div
                          key={code}
                          className="flex items-center gap-2 rounded-md border border-zinc-200 bg-zinc-50 px-3 py-1.5"
                        >
                          <span className="flex-1 text-sm">{getLangLabel(code)}</span>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            disabled={index === 0 || isBusy}
                            onClick={() => moveUp(index)}
                            title={t('tenant.language.moveUp')}
                          >
                            <ChevronUp size={14} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            disabled={index === selectedLanguages.length - 1 || isBusy}
                            onClick={() => moveDown(index)}
                            title={t('tenant.language.moveDown')}
                          >
                            <ChevronDown size={14} />
                          </Button>
                        </div>
                      )
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-zinc-500">{t('tenant.language.selectAtLeastOne')}</p>
                )}
              </div>
            </div>

            <SheetFooter className="border-t px-6 py-4">
              <Button onClick={() => setIsLanguagePickerOpen(false)}>{t('common.close')}</Button>
            </SheetFooter>
          </div>
        </SheetContent>
      </Sheet>

      <div>
        <p className="text-sm font-medium mb-2">{t('tenant.language.default')}</p>
        <Select
          value={defaultLanguage ?? ''}
          onValueChange={(value) => void handleDefaultLanguageChange(value)}
          disabled={selectedLanguages.length === 0 || updateDefaultMutation.isPending}
        >
          <SelectTrigger className="w-[240px]">
            {updateDefaultMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <SelectValue placeholder={t('tenant.language.defaultRequired')} />
            )}
          </SelectTrigger>
          <SelectContent>
            {selectedLanguages.map((code) => (
              <SelectItem key={code} value={code}>
                {getLangLabel(code)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  )
}
